import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { FIBONACCI_DECK } from '../../../domain/valueObjects/FibonacciDeck';
import { TSHIRT_DECK } from '../../../domain/valueObjects/TShirtDeck';
import { DeckType } from '../../../domain/entities/Room';
import { TokenSet } from '../../styles/tokens';
import { PokerCard } from './PokerCard';
import { useLanguage } from '../../contexts/LanguageContext';
import { getCardLabel } from '../../i18n/cardLabels';

const DeckWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
  padding: 8px;
`;

interface CardDeckProps {
  selectedCard: string | null;
  onSelect: (card: string) => void;
  disabled?: boolean;
  tokens: TokenSet;
  deckType?: DeckType;
}

const useMatchMedia = (query: string): boolean => {
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(query).matches : false
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    setMatches(mql.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [query]);
  return matches;
};

export const CardDeck: React.FC<CardDeckProps> = ({ selectedCard, onSelect, disabled, tokens, deckType = 'fibonacci' }) => {
  const { t } = useLanguage();
  const isCompact = useMatchMedia('(max-width: 900px)');
  const cardSize: 'sm' | 'md' = isCompact ? 'sm' : 'md';
  const cards: readonly string[] = deckType === 'tshirt' ? TSHIRT_DECK : FIBONACCI_DECK;
  return (
    <DeckWrapper>
      {cards.map(card => (
        <PokerCard
          key={card}
          value={getCardLabel(card, deckType, t)}
          tokens={tokens}
          selected={selectedCard === card}
          selectable={!disabled}
          size={cardSize}
          onClick={disabled ? undefined : () => onSelect(card)}
        />
      ))}
    </DeckWrapper>
  );
};
